import { FeatureFlag } from '@ps/web-core-enums';
import { take } from 'rxjs';

import { type WidgetEffectParams } from '@swf/core/store';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorStatusCode from '@swf/sports/utils/getErrorStatusCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { EventPageSuccessResponse, WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, select, rootState, widget } = params;
    const { clear, eventPageRequested }: WidgetDispatch = dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];

    sportsWidgetPollingBehavior(params).subscribe({
      next: (source) => {
        if (source === 'SIGN_UP') {
          clear();
        } else {
          eventPageRequested(source === 'PAGE_CHANGE');
        }
      }
    });

    widget.unmount.pipe(take(1)).subscribe({
      complete: () => {
        queueMicrotask(() => {
          clear();
        });
      }
    });

    if (!hasPreloaded(rootState)) {
      await eventPageRequested();
    }
  },
  async eventPageRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, request, widget, rootState, select } = params;
    const { eventId } = select.sports.getUrlParams(rootState);
    const sportsRequest = buildSportsRequest(request, rootState);
    const { eventPageLoadSuccess, eventPageLoadFailure }: WidgetDispatch = dispatch[widget.uid];

    try {
      const { body } = await sportsRequest<EventPageSuccessResponse>({
        path: `/event-page/${eventId}`,
        includeFeatures: [
          FeatureFlag.ISPBetBuilder,
          FeatureFlag.ISPEnableEventPagePopularBetBuilders,
          FeatureFlag.ISPEnableLiveStreaming,
          FeatureFlag.ISPEnableMarketMatchOddsNinety,
          FeatureFlag.ISPEnableMarketMatchOddsTwoUp,
          FeatureFlag.ISPEnableStarSub,
          FeatureFlag.ISPEnableAmericanFootballPlayerAltCompoundMarkets
        ],
        signal: getWidgetCancellationSignal(params)
      });

      eventPageLoadSuccess(body);
    } catch (error) {
      if (hasRequestAborted(error)) return;
      eventPageLoadFailure(getErrorStatusCode(error));
    }
    return;
  }
};
